import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import CheckValid from "../../Common/CheckValid";

// Asks for a reason (1 to 500 characters) before a destructive or publishing action.
// `onConfirm(reason)` returns a promise of { ok: true } or { ok: false, message }; the dialog shows
// the message and closes itself on success. Confirm is disabled until the reason is valid and
// while the request is in flight.
//
// `initialReason` and `reasonLocked` reopen the dialog on an action that may already have been
// applied (an earlier try got no clear answer): the reason is the one that try used and cannot be
// edited, so confirming again is the same request. `onConfirm` may return `unsure: true` to lock
// the reason after such an answer.
const ReasonDialog = ({
  show,
  title,
  explanation,
  confirmLabel = "Confirm",
  initialReason = "",
  reasonLocked = false,
  onConfirm,
  onClose,
}) => {
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [touched, setTouched] = useState(false);
  const [inFlight, setInFlight] = useState(false);
  const [failure, setFailure] = useState("");
  const [locked, setLocked] = useState(reasonLocked);
  // Read when the dialog opens; a change while it is open (an answer being recorded) must not reset it.
  const openingRef = useRef({ initialReason, reasonLocked });
  openingRef.current = { initialReason, reasonLocked };

  useEffect(() => {
    if (show) {
      setReason(openingRef.current.initialReason);
      setReasonError("");
      setTouched(false);
      setInFlight(false);
      setFailure("");
      setLocked(openingRef.current.reasonLocked);
    }
  }, [show]);

  const valid = reason.trim().length > 0 && reason.trim().length <= 500;

  const change = (value) => {
    if (locked) {
      return;
    }
    setReason(value);
    setFailure("");
    CheckValid(value, { type: "warranty_reason", error: setReasonError });
  };

  const confirm = async () => {
    setTouched(true);
    CheckValid(reason, { type: "warranty_reason", error: setReasonError });
    if (!valid || inFlight) {
      return;
    }
    setInFlight(true);
    setFailure("");
    const result = await onConfirm(reason.trim());
    setInFlight(false);
    if (result?.ok === true) {
      onClose();
    } else {
      setFailure(result?.message || "Something went wrong. Try again.");
      if (result?.unsure === true) {
        setLocked(true);
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={inFlight ? () => {} : onClose}
      backdrop={inFlight ? "static" : true}
      keyboard={!inFlight}
      className="commonModal"
      centered
    >
      <Modal.Header closeButton={!inFlight}>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {explanation && <p className="warranty-dialog-hint">{explanation}</p>}
        <div className="formloginset">
          <Form.Group className="mb-1" controlId="warrantyReason">
            <Form.Label>Reason</Form.Label>
            <Form.Control
              as="textarea"
              value={reason}
              maxLength={500}
              onChange={(event) => change(event.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="Say why, in a sentence or two (kept in the audit log)"
              disabled={inFlight}
              readOnly={locked}
              autoFocus
            />
            {touched && reasonError && (
              <div className="danger-colorset">{reasonError}</div>
            )}
          </Form.Group>
        </div>
        {locked && (
          <div className="warranty-banner is-warning" role="status">
            <span>
              We could not confirm what happened the last time. It may have gone through. Confirming again
              sends the same request, so the reason cannot be changed.
            </span>
          </div>
        )}
        {failure && (
          <div className="warranty-dialog-error" role="alert">
            {failure}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={inFlight}>
          Cancel
        </Button>
        <Button className="addbtncmn" onClick={confirm} disabled={!valid || inFlight}>
          {inFlight ? "Working" : confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReasonDialog;
